import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { WithdrawalReceipt } from '../utils/types';
import { standaloneNavigationState } from '../utils/qrEntry';
import { useStandaloneQr } from '../contexts/StandaloneQrContext';
import { errorBodyOf, RefusalCode } from '../services/orderLookupService';
import { getWithdrawal, declareErrorOf } from '../services/withdrawalService';
import {
  credentialsForOrder,
  currentCredentials,
} from '../services/portalCredentials';
import {
  resolveContext,
  saveContext,
  WithdrawalContext,
} from '../services/withdrawalContext';
import { loadReturnFlow } from '../services/returnFlowLoader';

// #149 (S3 of the withdrawal function): the confirmation of a declaration,
// `/withdrawal/:id`. The receipt comes from the continuation context (router
// state merged over this tab's stored copy) or from GET /withdrawals/{id}
// with the current credentials. While the case is not known yet (or a case A
// cancellation is running) it is polled, one request at a time. Case C
// continues into the return flow with `withdrawal_id`.
// #154 (audit #1, #3, #4, #5): the continuation resolves its credentials
// once (portalCredentials) and refuses a session of another shop / order;
// the loader's failures are typed: a coded refusal is shown as for a return,
// an expired session (403) or an inactive portal (410) is terminal (the
// continuation is disabled, the receipt stays readable, no recovery link),
// anything else has its own error copy with Retry - never the declaration's
// "could not register" (the declaration above IS registered).

export const POLL_MS = 2000;
export const MAX_POLLS = 10;

type Notice = 'sessionExpired' | 'portalInactive' | 'disabledByShop';

const cancellationRunning = (r: WithdrawalReceipt) =>
  r.case_code === 'A' &&
  (r.cancel_state === 'PENDING' || r.cancel_state === 'UNKNOWN_OUTCOME');

const needsPolling = (r: WithdrawalReceipt) =>
  r.next_step === 'PENDING' || cancellationRunning(r);

type ReadOutcome = 'ok' | 'terminal' | 'transient' | 'none' | 'stale';

type ContinueError =
  | { kind: 'failed' }
  | { kind: 'refused'; code: RefusalCode };

const ReceiptView: React.FC<{ id: number }> = ({ id }) => {
  const { t } = useTranslation(['translation', 'dynamic']);
  const navigate = useNavigate();
  const location = useLocation();
  const { active: standaloneQr, handoff, account } = useStandaloneQr();
  const [context] = useState<WithdrawalContext | null>(() =>
    resolveContext(id, location.state)
  );
  const [receipt, setReceipt] = useState<WithdrawalReceipt | null>(
    context?.receipt ?? null
  );
  const [loading, setLoading] = useState(context === null);
  const [unavailable, setUnavailable] = useState(false);
  const [notice, setNotice] = useState<Notice | null>(null);
  const receiptRef = useRef<WithdrawalReceipt | null>(receipt);
  const generation = useRef(0);

  useEffect(() => {
    const gen = ++generation.current;
    const current = () => gen === generation.current;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let polls = 0;

    const read = async (): Promise<ReadOutcome> => {
      const credentials = currentCredentials(standaloneQr);
      if (credentials === null) return 'none';
      try {
        const response = await getWithdrawal(
          id,
          credentials.token,
          credentials.shopId
        );
        if (!current()) return 'stale';
        if (response.ok) {
          const next: WithdrawalReceipt = await response.json();
          if (!current()) return 'stale';
          receiptRef.current = next;
          setReceipt(next);
          if (context) saveContext({ ...context, receipt: next });
          return 'ok';
        }
        const body = await errorBodyOf(response);
        if (!current()) return 'stale';
        if (response.status === 410) {
          setNotice(
            declareErrorOf(response.status, body) === 'disabledByShop'
              ? 'disabledByShop'
              : 'portalInactive'
          );
          return 'terminal';
        }
        if (response.status === 403 || response.status === 404) {
          setNotice('sessionExpired');
          return 'terminal';
        }
        return 'transient';
      } catch {
        return current() ? 'transient' : 'stale';
      }
    };

    // sequential: the next request is armed only after an answer
    const schedule = () => {
      const r = receiptRef.current;
      if (!current() || !r || !needsPolling(r) || polls >= MAX_POLLS) return;
      timer = setTimeout(tick, POLL_MS);
    };
    const tick = async () => {
      polls += 1;
      const outcome = await read();
      if (outcome === 'ok' || outcome === 'transient') schedule();
    };

    if (receiptRef.current !== null) {
      schedule();
    } else if (currentCredentials(standaloneQr) === null) {
      // nothing to read the receipt with: the e-mail is the durable copy
      setLoading(false);
      setUnavailable(true);
    } else {
      read().then(outcome => {
        if (outcome === 'stale' || !current()) return;
        setLoading(false);
        if (outcome === 'ok') schedule();
        else if (outcome === 'none' || outcome === 'transient') {
          setUnavailable(true);
        }
      });
    }
    return () => {
      generation.current += 1;
      if (timer !== undefined) clearTimeout(timer);
    };
    // per mount (the page is keyed by the receipt id)
  }, [id, standaloneQr, context]);

  // the case C continuation: one loader per click burst; leaving the page
  // invalidates it
  const [continuing, setContinuing] = useState(false);
  const [continueError, setContinueError] = useState<ContinueError | null>(
    null
  );
  const inFlight = useRef(false);
  const attempt = useRef(0);
  useEffect(
    () => () => {
      attempt.current += 1;
    },
    []
  );
  // terminal (session expired, portal inactive): no further continuation
  const blocked = notice !== null;
  const terminal = (kind: Notice) => {
    setContinueError(null);
    setNotice(kind);
  };

  const continueToReturn = async () => {
    if (inFlight.current || blocked || !receipt || !context?.order) return;
    inFlight.current = true;
    const mine = ++attempt.current;
    setContinueError(null);
    setContinuing(true);
    const standalone = standaloneQr;
    const order = context.order;
    try {
      // the one resolver of the case C flow (loading here, uploads and
      // submission on the methods step); a session of another shop or
      // another order, or a receipt of another order, is never continued
      const credentials =
        receipt.order_id === order.id
          ? credentialsForOrder(standalone, order.shop_id, order.id)
          : null;
      if (credentials === null) {
        terminal('sessionExpired');
        return;
      }
      const result = await loadReturnFlow(
        { token: credentials.token, order_details: order },
        {
          proof: context.proof ?? null,
          shop: context.shop ?? null,
          standalone,
          credentials,
        }
      );
      if (mine !== attempt.current) return;
      if (result.kind === 'ok') {
        navigate('/order-details', {
          state: {
            ...result.state,
            ...(standalone ? standaloneNavigationState(handoff, account) : {}),
            withdrawal_id: receipt.id,
            withdrawal_receipt: receipt,
          },
        });
      } else if (
        result.kind === 'portalInactive' ||
        result.kind === 'disabledByShop'
      ) {
        terminal(result.kind);
      } else if (result.code !== null) {
        // a recognized refusal code: shown as the lookup shows it
        setContinueError({ kind: 'refused', code: result.code });
      } else if (result.status === 403) {
        terminal('sessionExpired');
      } else {
        // Codex r1 #3: an uncoded refusal is no refusal of the return: the
        // continuation copy, with Retry
        setContinueError({ kind: 'failed' });
      }
    } catch (error) {
      console.error(error);
      if (mine === attempt.current) setContinueError({ kind: 'failed' });
    } finally {
      inFlight.current = false;
      if (mine === attempt.current) setContinuing(false);
    }
  };

  // #154: a refusal of the continuation, as the lookup shows it for a return
  // (literal keys, orphan-key gate)
  const refusalText = (code: RefusalCode): string => {
    switch (code) {
      case 'ORDER_NOT_FULFILLED':
        return t('forms.lookupNotFulfilled');
      case 'SECOND_RETURN_NOT_ALLOWED':
        return t('forms.secondReturnNotAllowed');
      case 'ALREADY_RETURNED':
        return t('forms.lookupAlreadyReturned');
      case 'RETURN_WINDOW_PASSED':
        return t('forms.lookupWindowPassed');
      case 'RETURN_DENIED_BY_RULE':
        return t('forms.lookupDeniedByRule');
    }
  };
  const continueErrorText = (error: ContinueError): string =>
    error.kind === 'failed'
      ? t('withdrawal.continueFailed')
      : refusalText(error.code);

  const noticeText = (kind: Notice): string =>
    kind === 'disabledByShop'
      ? t('withdrawal.disabledByShop')
      : kind === 'portalInactive'
        ? t('withdrawal.portalInactive')
        : t('withdrawal.sessionExpired');
  const noticeBlock = notice && (
    <div
      className={
        notice === 'disabledByShop'
          ? 'xc-notice xc-notice--withdraw'
          : 'xc-notice xc-notice--warn'
      }
      role="alert"
    >
      {noticeText(notice)}
    </div>
  );

  if (loading) {
    return (
      <div className="xc-screen">
        <div className="xc-status" role="status">
          <span className="xc-spinner" aria-hidden="true"></span>
          {t('tracking.loading')}
        </div>
      </div>
    );
  }

  if (receipt === null) {
    return (
      <div className="xc-screen">
        {noticeBlock ??
          (unavailable && (
            <div className="xc-notice xc-notice--warn" role="alert">
              {t('withdrawal.receiptUnavailable')}
            </div>
          ))}
      </div>
    );
  }

  const declaredAt = (() => {
    const date = new Date(receipt.declared_at);
    const locale = String(t('buttons.locale'));
    try {
      return new Intl.DateTimeFormat(locale, {
        dateStyle: 'long',
        timeStyle: 'short',
        timeZone: receipt.time_zone,
      }).format(date);
    } catch {
      return new Intl.DateTimeFormat(locale, {
        dateStyle: 'long',
        timeStyle: 'short',
      }).format(date);
    }
  })();

  const legalRefund = <p className="xc-help">{t('withdrawal.legalRefund')}</p>;
  const pending = <p className="xc-help">{t('withdrawal.pending')}</p>;

  // one switch, literal keys (orphan-key gate); never "on its way"
  const caseText = (): React.ReactNode => {
    if (receipt.rejected) {
      return <p className="xc-help">{t('withdrawal.rejected')}</p>;
    }
    if (receipt.next_step === 'PENDING') return pending;
    switch (receipt.case_code) {
      case 'A':
        return (
          <>
            <p className="xc-help">{t('withdrawal.caseA')}</p>
            {cancellationRunning(receipt) && (
              <p className="xc-help">{t('withdrawal.caseAProcessing')}</p>
            )}
          </>
        );
      case 'B':
        return legalRefund;
      case 'C':
        return (
          <>
            {legalRefund}
            <p className="xc-help">{t('withdrawal.caseCReturn')}</p>
            {context?.order ? (
              <button
                type="button"
                className="xc-btn xc-btn--primary xc-btn--block"
                onClick={continueToReturn}
                disabled={continuing || blocked}
              >
                {continuing ? (
                  <>
                    <span className="xc-spinner" aria-hidden="true"></span>
                    {t('buttons.submitting')}
                  </>
                ) : (
                  t('withdrawal.continueToReturn')
                )}
              </button>
            ) : (
              <p className="xc-help">{t('withdrawal.continueFromEmail')}</p>
            )}
          </>
        );
      default:
        return pending;
    }
  };

  return (
    <div className="xc-screen">
      <div className="xc-title-row">
        <h2 className="xc-title">
          {t('withdrawal.registeredAt', { date: declaredAt })}
        </h2>
      </div>
      <div className="xc-card">{caseText()}</div>
      {continueError && (
        <div className="xc-notice xc-notice--error" role="alert">
          <p className="xc-notice__text">{continueErrorText(continueError)}</p>
          {continueError.kind === 'failed' && !blocked && (
            <button
              type="button"
              className="xc-btn xc-btn--sm"
              onClick={continueToReturn}
            >
              {t('buttons.retry')}
            </button>
          )}
        </div>
      )}
      {noticeBlock}
      <div className="xc-card xc-card--quiet">
        <p className="xc-help">
          {t('withdrawal.emailSent', { email: receipt.email })}
        </p>
      </div>
    </div>
  );
};

// keyed by the id: another declaration mounts a fresh page (its own
// generation, timers and continuation attempt)
const WithdrawalReceiptPage: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const receiptId = Number(id);
  if (!Number.isInteger(receiptId) || receiptId <= 0) {
    return (
      <div className="xc-screen">
        <div className="xc-notice xc-notice--warn" role="alert">
          {t('withdrawal.receiptUnavailable')}
        </div>
      </div>
    );
  }
  return <ReceiptView key={receiptId} id={receiptId} />;
};

export default WithdrawalReceiptPage;
